import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { locale } from 'dayjs';
import { configure } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { ROUTES } from 'configs/routes';
import { cssVariablesResolver, theme } from 'configs/theme';
import { withRole } from 'hocs/withRole';
import CenterFormLayout from 'layouts/CenterFormLayout';
import { GlobalLoader } from 'layouts/GlobalLoader';
import LkLayout from 'layouts/LkLayout';
import LoginLayout from 'layouts/LoginLayout';
import RegisterLayout from 'layouts/RegisterLayout';
import RootLayout from 'layouts/RootLayout';
import ErrorPage from 'pages/ErrorPage';
import { useAppConfigBootstrap } from 'stores/global/RootStore/context';
import { createDynamicTheme } from 'utils/createDynamicTheme';
import { DeviceProvider } from 'utils/devices';
import { liveVh } from 'utils/liveVh';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/carousel/styles.css';
import 'styles/globalStyles.scss';

import 'dayjs/locale/ru';

import './scripts';

liveVh();

locale('ru');

configure({
  enforceActions: 'always',
  computedRequiresReaction: true,
  reactionRequiresObservable: true,
  observableRequiresReaction: true,
  useProxies: 'ifavailable',
});

const LoginPage = React.lazy(() => import('pages/LoginPage'));
const RegistrationPage = React.lazy(() => import('pages/registration/RegistrationPage'));
const RegistrationSetPasswordPage = React.lazy(() => import('pages/registration/RegistrationSetPasswordPage'));
const RecoveryStartPage = React.lazy(() => import('pages/recovery/RecoveryStartPage'));
const RecoverySetPasswordPage = React.lazy(() => import('pages/recovery/RecoverySetPasswordPage'));

const EmployeesPage = withRole(
  React.lazy(() => import('pages/lk/EmployeesPage')),
  new Set(['director', 'fixator']),
  ROUTES.lk.clients.create(),
);
const ClientsPage = React.lazy(() => import('pages/lk/ClientsPage'));
const ClientDetailPage = React.lazy(() => import('pages/lk/ClientDetailPage'));
const LeadsPage = React.lazy(() => import('pages/lk/LeadsPage'));
const LotsPage = React.lazy(() => import('pages/lk/LotsPage'));
const MeetingsPage = React.lazy(() => import('pages/lk/MeetingsPage'));
const AboutPage = React.lazy(() => import('pages/lk/AboutPage'));
const DocumentsPage = React.lazy(() => import('pages/lk/DocumentsPage'));
const ProjectDetailPage = React.lazy(() => import('pages/lk/ProjectDetailPage'));
const ProjectsPage = React.lazy(() => import('pages/lk/ProjectsPage/ProjectsPage'));
const ProfilePage = React.lazy(() => import('pages/lk/ProfilePage'));
const LkIndexRedirect = React.lazy(() => import('pages/lk/LkIndexRedirect'));

const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      children: [
        {
          index: true,
          element: <Navigate to={ROUTES.login.create()} replace />,
        },
        {
          element: <LoginLayout />,
          children: [
            {
              path: ROUTES.login.path,
              element: <LoginPage />,
            },
          ],
        },
        {
          element: <RegisterLayout />,
          children: [
            {
              path: ROUTES.registration.path,
              element: <RegistrationPage />,
            },
          ],
        },
        {
          element: <CenterFormLayout />,
          children: [
            {
              path: ROUTES.registration.setPassword.path,
              element: <RegistrationSetPasswordPage />,
            },
          ],
        },
        {
          path: ROUTES.recovery.path,
          element: <CenterFormLayout />,
          children: [
            {
              index: true,
              element: <RecoveryStartPage />,
            },
            {
              path: ROUTES.recovery.setPassword.path,
              element: <RecoverySetPasswordPage />,
            },
          ],
        },
        {
          path: ROUTES.lk.path,
          element: <LkLayout />,
          children: [
            {
              index: true,
              element: <LkIndexRedirect />,
            },
            {
              path: ROUTES.lk.profile.path,
              element: <ProfilePage />,
            },
            {
              path: ROUTES.lk.employees.path,
              element: <EmployeesPage />,
            },
            {
              path: ROUTES.lk.clients.path,
              element: <ClientsPage />,
            },
            {
              path: ROUTES.lk.projects.path,
              element: <ProjectsPage />,
            },
            {
              path: ROUTES.lk.projects.detail.path,
              element: <ProjectDetailPage />,
            },
            {
              path: ROUTES.lk.clients.detail.path,
              element: <ClientDetailPage />,
            },
            {
              path: ROUTES.lk.leads.detail.path,
              element: <LeadsPage />,
            },
            {
              path: ROUTES.lk.leads.path,
              element: <LeadsPage />,
            },
            {
              path: ROUTES.lk.lots.path,
              element: <LotsPage />,
            },
            {
              path: ROUTES.lk.meetings.path,
              element: <MeetingsPage />,
            },
            {
              path: ROUTES.lk.about.path,
              element: <AboutPage />,
            },
            {
              path: ROUTES.lk.documents.path,
              element: <DocumentsPage />,
            },
          ],
        },
      ],
      errorElement: <ErrorPage />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

export const App = observer(() => {
  const { bootstrapUi, isLoading, error } = useAppConfigBootstrap();

  const dynamicTheme = React.useMemo(() => createDynamicTheme(theme, bootstrapUi), [bootstrapUi]);

  return (
    <MantineProvider theme={dynamicTheme} cssVariablesResolver={cssVariablesResolver}>
      <DeviceProvider>
        <DatesProvider settings={{ locale: 'ru' }}>
          {isLoading || error ? (
            <GlobalLoader error={error} />
          ) : (
            <RouterProvider
              router={router}
              future={{
                v7_startTransition: true,
              }}
            />
          )}
        </DatesProvider>
      </DeviceProvider>
    </MantineProvider>
  );
});
